import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Plus,
  Trash2,
  ChevronDown,
  ChevronRight,
  Save,
  Loader2,
  ArrowLeft,
  GripVertical,
  Film,
  Lock,
  Eye,
} from 'lucide-react';
import { adminFetch } from '@/utils/adminAuth';

/**
 * Admin editor for the Pandeiro Juggling course curriculum.
 * - CRUD chapters (number, title, subtitle, description, icon, is_published)
 * - CRUD lessons within a chapter (title, description, video_url, thumbnail_url,
 *   duration, is_preview, order)
 * - Edit course settings (title/subtitle/description/pricing/hero/intro video)
 *
 * Backend: /api/pandeiro-course/admin/*
 */
export default function AdminPandeiroCurriculum() {
  const [chapters, setChapters] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingChapterId, setSavingChapterId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [chRes, stRes] = await Promise.all([
        adminFetch('/pandeiro-course/admin/chapters'),
        adminFetch('/pandeiro-course/admin/settings'),
      ]);
      if (!chRes.ok) throw new Error('Failed to load chapters');
      if (!stRes.ok) throw new Error('Failed to load settings');
      const chData = await chRes.json();
      const stData = await stRes.json();
      // Ensure lessons array exists & is sorted by order
      const normalized = (chData || []).map((c) => ({
        ...c,
        lessons: (c.lessons || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
      }));
      setChapters(normalized);
      setSettings(stData);
    } catch (e) {
      toast.error(e.message || 'Failed to load curriculum');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // ---------- Settings ----------
  const updateSettingField = (field, value) => {
    setSettings((s) => ({ ...s, [field]: value }));
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      // Only send fields the backend accepts (strip id/_id)
      const payload = {
        title: settings.title || '',
        subtitle: settings.subtitle || '',
        description: settings.description || '',
        price: Number(settings.price) || 0,
        price_period: settings.price_period || 'month',
        one_time_price: Number(settings.one_time_price) || 0,
        hero_image: settings.hero_image || null,
        intro_video_url: settings.intro_video_url || null,
        is_published: !!settings.is_published,
      };
      const res = await adminFetch('/pandeiro-course/admin/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Failed to save settings');
      toast.success('Course settings saved');
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setSavingSettings(false);
    }
  };

  // ---------- Chapter helpers ----------
  const emptyChapter = () => ({
    number: (chapters[chapters.length - 1]?.number || 0) + 1,
    title: 'New Chapter',
    subtitle: '',
    description: '',
    icon: 'Hand',
    is_published: true,
    lessons: [],
  });

  const addChapter = async () => {
    try {
      const res = await adminFetch('/pandeiro-course/admin/chapter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emptyChapter()),
      });
      if (!res.ok) throw new Error('Failed to create chapter');
      await load();
      toast.success('Chapter added');
    } catch (e) {
      toast.error(e.message || 'Add failed');
    }
  };

  const updateChapterField = (idx, field, value) => {
    setChapters((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const saveChapter = async (idx) => {
    const ch = chapters[idx];
    if (!ch?.id) return;
    setSavingChapterId(ch.id);
    try {
      const payload = {
        number: Number(ch.number) || 0,
        title: ch.title || '',
        subtitle: ch.subtitle || '',
        description: ch.description || '',
        icon: ch.icon || null,
        is_published: !!ch.is_published,
        lessons: (ch.lessons || []).map((l, i) => ({
          title: l.title || '',
          description: l.description || '',
          video_url: l.video_url || null,
          thumbnail_url: l.thumbnail_url || null,
          duration: l.duration || null,
          is_preview: !!l.is_preview,
          order: Number(l.order ?? i) || i,
        })),
      };
      const res = await adminFetch(`/pandeiro-course/admin/chapter/${ch.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Failed to save chapter');
      toast.success(`Chapter ${ch.number} saved`);
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setSavingChapterId(null);
    }
  };

  const deleteChapter = async (idx) => {
    const ch = chapters[idx];
    if (!ch?.id) return;
    if (!window.confirm(`Delete "${ch.title}" and all its lessons?`)) return;
    try {
      const res = await adminFetch(`/pandeiro-course/admin/chapter/${ch.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete chapter');
      toast.success('Chapter deleted');
      await load();
    } catch (e) {
      toast.error(e.message || 'Delete failed');
    }
  };

  // ---------- Lesson helpers ----------
  const addLesson = (chapterIdx) => {
    setChapters((prev) => {
      const next = [...prev];
      const ch = next[chapterIdx];
      const lessons = ch.lessons || [];
      lessons.push({
        title: 'New Lesson',
        description: '',
        video_url: '',
        thumbnail_url: '',
        duration: '',
        is_preview: false,
        order: lessons.length,
      });
      next[chapterIdx] = { ...ch, lessons };
      return next;
    });
  };

  const updateLessonField = (chapterIdx, lessonIdx, field, value) => {
    setChapters((prev) => {
      const next = [...prev];
      const ch = next[chapterIdx];
      const lessons = [...(ch.lessons || [])];
      lessons[lessonIdx] = { ...lessons[lessonIdx], [field]: value };
      next[chapterIdx] = { ...ch, lessons };
      return next;
    });
  };

  const removeLesson = (chapterIdx, lessonIdx) => {
    setChapters((prev) => {
      const next = [...prev];
      const ch = next[chapterIdx];
      const lessons = (ch.lessons || []).filter((_, i) => i !== lessonIdx);
      // reorder
      lessons.forEach((l, i) => (l.order = i));
      next[chapterIdx] = { ...ch, lessons };
      return next;
    });
  };

  const moveLesson = (chapterIdx, lessonIdx, dir) => {
    setChapters((prev) => {
      const next = [...prev];
      const ch = next[chapterIdx];
      const lessons = [...(ch.lessons || [])];
      const j = lessonIdx + dir;
      if (j < 0 || j >= lessons.length) return prev;
      [lessons[lessonIdx], lessons[j]] = [lessons[j], lessons[lessonIdx]];
      lessons.forEach((l, i) => (l.order = i));
      next[chapterIdx] = { ...ch, lessons };
      return next;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-amber-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-zinc-950/90 backdrop-blur border-b border-zinc-800 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link
            to="/admin/dashboard"
            className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white"
            data-testid="pandeiro-admin-back"
          >
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </Link>
          <div className="h-5 w-px bg-zinc-700" />
          <h1 className="text-xl font-black">Pandeiro Juggling – Curriculum Editor</h1>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/pandeiro-juggling"
            target="_blank"
            rel="noreferrer"
            className="px-3 py-1.5 rounded-lg border border-zinc-700 text-sm hover:bg-zinc-900"
            data-testid="pandeiro-admin-view-page"
          >
            View public page
          </Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-10">
        {/* Course Settings */}
        {settings && (
          <section
            id="settings"
            className="rounded-2xl border border-zinc-800 bg-zinc-950 p-6"
            data-testid="pandeiro-settings-section"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">Course settings</h2>
              <button
                onClick={saveSettings}
                disabled={savingSettings}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-bold disabled:opacity-50"
                data-testid="pandeiro-settings-save"
              >
                {savingSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save settings
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Title">
                <input
                  value={settings.title || ''}
                  onChange={(e) => updateSettingField('title', e.target.value)}
                  className={inputCls}
                  data-testid="pandeiro-settings-title"
                />
              </Field>
              <Field label="Subtitle">
                <input
                  value={settings.subtitle || ''}
                  onChange={(e) => updateSettingField('subtitle', e.target.value)}
                  className={inputCls}
                  data-testid="pandeiro-settings-subtitle"
                />
              </Field>
              <Field label="Description" full>
                <textarea
                  value={settings.description || ''}
                  onChange={(e) => updateSettingField('description', e.target.value)}
                  rows={4}
                  className={inputCls}
                  data-testid="pandeiro-settings-description"
                />
              </Field>
              <Field label="Monthly price">
                <input
                  type="number"
                  step="0.01"
                  value={settings.price ?? 0}
                  onChange={(e) => updateSettingField('price', e.target.value)}
                  className={inputCls}
                  data-testid="pandeiro-settings-price"
                />
              </Field>
              <Field label="Price period">
                <input
                  value={settings.price_period || 'month'}
                  onChange={(e) => updateSettingField('price_period', e.target.value)}
                  className={inputCls}
                  data-testid="pandeiro-settings-price-period"
                />
              </Field>
              <Field label="One-time (lifetime) price">
                <input
                  type="number"
                  step="0.01"
                  value={settings.one_time_price ?? 0}
                  onChange={(e) => updateSettingField('one_time_price', e.target.value)}
                  className={inputCls}
                  data-testid="pandeiro-settings-one-time-price"
                />
              </Field>
              <Field label="Hero image URL">
                <input
                  value={settings.hero_image || ''}
                  onChange={(e) => updateSettingField('hero_image', e.target.value)}
                  className={inputCls}
                  data-testid="pandeiro-settings-hero-image"
                />
              </Field>
              <Field label="Intro video URL" full>
                <input
                  value={settings.intro_video_url || ''}
                  onChange={(e) => updateSettingField('intro_video_url', e.target.value)}
                  className={inputCls}
                  data-testid="pandeiro-settings-intro-video"
                />
              </Field>
              <Field label="Published">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={!!settings.is_published}
                    onChange={(e) => updateSettingField('is_published', e.target.checked)}
                    data-testid="pandeiro-settings-published"
                  />
                  Visible on public page
                </label>
              </Field>
            </div>
          </section>
        )}

        {/* Chapters */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold">
              Chapters <span className="text-zinc-500 font-normal">({chapters.length})</span>
            </h2>
            <button
              onClick={addChapter}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-bold"
              data-testid="pandeiro-add-chapter"
            >
              <Plus className="w-4 h-4" /> Add chapter
            </button>
          </div>

          <div className="space-y-3">
            {chapters.map((ch, chapterIdx) => {
              const isOpen = expanded === ch.id;
              return (
                <div
                  key={ch.id}
                  className="rounded-xl border border-zinc-800 bg-zinc-950"
                  data-testid={`pandeiro-chapter-row-${ch.number}`}
                >
                  <div className="flex items-center gap-3 p-4">
                    <button
                      onClick={() => setExpanded(isOpen ? null : ch.id)}
                      className="text-zinc-400 hover:text-white"
                      data-testid={`pandeiro-chapter-toggle-${ch.number}`}
                    >
                      {isOpen ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                    </button>
                    <input
                      type="number"
                      value={ch.number}
                      onChange={(e) => updateChapterField(chapterIdx, 'number', Number(e.target.value) || 0)}
                      className="w-16 bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-sm"
                      data-testid={`pandeiro-chapter-number-${ch.number}`}
                    />
                    <input
                      value={ch.title}
                      onChange={(e) => updateChapterField(chapterIdx, 'title', e.target.value)}
                      className="flex-1 bg-zinc-900 border border-zinc-800 rounded px-3 py-1.5 text-sm font-semibold"
                      data-testid={`pandeiro-chapter-title-${ch.number}`}
                    />
                    <span className="text-xs text-zinc-500">
                      {ch.lessons?.length || 0} lesson{(ch.lessons?.length || 0) === 1 ? '' : 's'}
                    </span>
                    <button
                      onClick={() => saveChapter(chapterIdx)}
                      disabled={savingChapterId === ch.id}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-black text-sm font-bold disabled:opacity-50"
                      data-testid={`pandeiro-chapter-save-${ch.number}`}
                    >
                      {savingChapterId === ch.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                      Save
                    </button>
                    <button
                      onClick={() => deleteChapter(chapterIdx)}
                      className="p-2 rounded-lg text-red-400 hover:bg-red-500/10"
                      title="Delete chapter"
                      data-testid={`pandeiro-chapter-delete-${ch.number}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  {isOpen && (
                    <div className="border-t border-zinc-800 p-4 space-y-4">
                      <div className="grid md:grid-cols-2 gap-3">
                        <Field label="Subtitle">
                          <input
                            value={ch.subtitle || ''}
                            onChange={(e) => updateChapterField(chapterIdx, 'subtitle', e.target.value)}
                            className={inputCls}
                            data-testid={`pandeiro-chapter-subtitle-${ch.number}`}
                          />
                        </Field>
                        <Field label="Icon (lucide name)">
                          <input
                            value={ch.icon || ''}
                            onChange={(e) => updateChapterField(chapterIdx, 'icon', e.target.value)}
                            className={inputCls}
                            placeholder="Hand, Music, Zap…"
                            data-testid={`pandeiro-chapter-icon-${ch.number}`}
                          />
                        </Field>
                        <Field label="Description" full>
                          <textarea
                            value={ch.description || ''}
                            onChange={(e) => updateChapterField(chapterIdx, 'description', e.target.value)}
                            rows={3}
                            className={inputCls}
                            data-testid={`pandeiro-chapter-description-${ch.number}`}
                          />
                        </Field>
                        <Field label="Published">
                          <label className="inline-flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={!!ch.is_published}
                              onChange={(e) => updateChapterField(chapterIdx, 'is_published', e.target.checked)}
                              data-testid={`pandeiro-chapter-published-${ch.number}`}
                            />
                            Visible on public page
                          </label>
                        </Field>
                      </div>

                      {/* Lessons */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-bold text-zinc-300">
                            Lessons <span className="text-zinc-500 font-normal">({ch.lessons?.length || 0})</span>
                          </h4>
                          <button
                            onClick={() => addLesson(chapterIdx)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-xs font-semibold"
                            data-testid={`pandeiro-add-lesson-${ch.number}`}
                          >
                            <Plus className="w-3.5 h-3.5" /> Add lesson
                          </button>
                        </div>
                        <div className="space-y-2">
                          {(ch.lessons || []).map((lesson, lessonIdx) => (
                            <div
                              key={lessonIdx}
                              className="rounded-lg border border-zinc-800 bg-zinc-900 p-3"
                              data-testid={`pandeiro-lesson-${ch.number}-${lessonIdx}`}
                            >
                              <div className="flex items-start gap-2">
                                <div className="flex flex-col gap-0.5 pt-1">
                                  <button
                                    onClick={() => moveLesson(chapterIdx, lessonIdx, -1)}
                                    className="text-zinc-500 hover:text-white text-xs"
                                    title="Move up"
                                  >
                                    ▲
                                  </button>
                                  <button
                                    onClick={() => moveLesson(chapterIdx, lessonIdx, 1)}
                                    className="text-zinc-500 hover:text-white text-xs"
                                    title="Move down"
                                  >
                                    ▼
                                  </button>
                                </div>
                                <GripVertical className="w-4 h-4 text-zinc-600 mt-1" />
                                <div className="flex-1 space-y-2">
                                  <div className="grid md:grid-cols-2 gap-2">
                                    <Field label="Title">
                                      <input
                                        value={lesson.title || ''}
                                        onChange={(e) =>
                                          updateLessonField(chapterIdx, lessonIdx, 'title', e.target.value)
                                        }
                                        className={inputCls}
                                        data-testid={`pandeiro-lesson-title-${ch.number}-${lessonIdx}`}
                                      />
                                    </Field>
                                    <Field label="Duration (e.g. 5:12)">
                                      <input
                                        value={lesson.duration || ''}
                                        onChange={(e) =>
                                          updateLessonField(chapterIdx, lessonIdx, 'duration', e.target.value)
                                        }
                                        className={inputCls}
                                        data-testid={`pandeiro-lesson-duration-${ch.number}-${lessonIdx}`}
                                      />
                                    </Field>
                                    <Field label="Description" full>
                                      <textarea
                                        value={lesson.description || ''}
                                        onChange={(e) =>
                                          updateLessonField(chapterIdx, lessonIdx, 'description', e.target.value)
                                        }
                                        rows={2}
                                        className={inputCls}
                                        data-testid={`pandeiro-lesson-desc-${ch.number}-${lessonIdx}`}
                                      />
                                    </Field>
                                    <Field label="Video URL">
                                      <div className="relative">
                                        <Film className="w-4 h-4 text-zinc-500 absolute left-2 top-2.5" />
                                        <input
                                          value={lesson.video_url || ''}
                                          onChange={(e) =>
                                            updateLessonField(chapterIdx, lessonIdx, 'video_url', e.target.value)
                                          }
                                          className={inputCls + ' pl-7'}
                                          placeholder="https://…"
                                          data-testid={`pandeiro-lesson-video-${ch.number}-${lessonIdx}`}
                                        />
                                      </div>
                                    </Field>
                                    <Field label="Thumbnail URL">
                                      <input
                                        value={lesson.thumbnail_url || ''}
                                        onChange={(e) =>
                                          updateLessonField(chapterIdx, lessonIdx, 'thumbnail_url', e.target.value)
                                        }
                                        className={inputCls}
                                        data-testid={`pandeiro-lesson-thumb-${ch.number}-${lessonIdx}`}
                                      />
                                    </Field>
                                  </div>
                                  <div className="flex items-center gap-4 text-xs text-zinc-400">
                                    <label className="inline-flex items-center gap-2">
                                      <input
                                        type="checkbox"
                                        checked={!!lesson.is_preview}
                                        onChange={(e) =>
                                          updateLessonField(chapterIdx, lessonIdx, 'is_preview', e.target.checked)
                                        }
                                        data-testid={`pandeiro-lesson-preview-${ch.number}-${lessonIdx}`}
                                      />
                                      {lesson.is_preview ? (
                                        <span className="inline-flex items-center gap-1 text-green-400">
                                          <Eye className="w-3 h-3" /> Free preview
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center gap-1">
                                          <Lock className="w-3 h-3" /> Members only
                                        </span>
                                      )}
                                    </label>
                                    <span>Order: {lesson.order ?? lessonIdx}</span>
                                  </div>
                                </div>
                                <button
                                  onClick={() => removeLesson(chapterIdx, lessonIdx)}
                                  className="p-1.5 rounded text-red-400 hover:bg-red-500/10"
                                  title="Remove lesson"
                                  data-testid={`pandeiro-lesson-delete-${ch.number}-${lessonIdx}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                          {(ch.lessons || []).length === 0 && (
                            <p className="text-xs text-zinc-500 italic">No lessons yet. Click “Add lesson” above.</p>
                          )}
                        </div>
                        <p className="text-[11px] text-zinc-500 mt-2">
                          Remember to click <strong>Save</strong> on the chapter header after adding or editing lessons.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {chapters.length === 0 && (
              <p className="text-sm text-zinc-500 italic">
                No chapters yet. Click <strong>Add chapter</strong> to create the first one.
              </p>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

const inputCls =
  'w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-amber-500';

const Field = ({ label, children, full }) => (
  <div className={full ? 'md:col-span-2' : ''}>
    <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-1">{label}</label>
    {children}
  </div>
);
